import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { weddingData } from '../mockData';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, toggleLanguage } = useLanguage();
  const location = useLocation();
  const content = weddingData[language].nav;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const navItems = [
    { path: '/', label: content.home },
    { path: '/our-story', label: content.ourStory },
    { path: '/gallery', label: content.gallery },
    { path: '/program', label: content.program },
    { path: '/rsvp', label: content.rsvp },
    { path: '/maps', label: content.maps },
    { path: '/infos-ci', label: content.infosCI },
    { path: '/guestbook', label: content.guestbook },
    { path: '/accommodation', label: content.accommodation },
    { path: '/gifts', label: content.gifts },
    { path: '/faq', label: content.faq }
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-surface/98 backdrop-blur-xl shadow-soft'
            : 'bg-gradient-to-b from-white/60 to-transparent'
        }`}
      >
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 sm:h-20">
            
            {/* Logo */}
            <Link
              to="/"
              className="text-xl sm:text-2xl font-playfair font-bold text-plum-900 hover:text-gold-600 transition-colors relative z-50"
            >
              N <span className="text-gold-500">&</span> C
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden xl:flex items-center gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-3 py-2 text-sm font-montserrat font-medium tracking-wide rounded-lg transition-all duration-300 hover:bg-lilac-100/50 hover:text-gold-600 ${
                    location.pathname === item.path
                      ? 'text-gold-600 bg-gold-50/50'
                      : 'text-plum-800'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Right Side Actions */}
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-montserrat font-semibold transition-all duration-300 ${
                  isScrolled
                    ? 'bg-lilac-100/80 text-plum-800 hover:bg-gold-100 hover:text-gold-700'
                    : 'bg-white/70 text-plum-800 hover:bg-white hover:text-gold-700'
                }`}
              >
                <Globe size={16} />
                <span className="hidden sm:inline">{language.toUpperCase()}</span>
              </button>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`xl:hidden p-2.5 rounded-xl transition-all duration-300 relative z-50 ${
                  isMobileMenuOpen
                    ? 'bg-plum-900 text-white'
                    : isScrolled
                    ? 'bg-lilac-100/80 text-plum-800 hover:bg-lilac-200'
                    : 'bg-white/70 text-plum-800 hover:bg-white'
                }`}
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 xl:hidden transition-all duration-500 ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-plum-900/60 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />
        
        {/* Menu Panel */}
        <div
          className={`absolute top-0 right-0 h-full w-full max-w-sm bg-surface shadow-2xl transition-transform duration-500 ease-out ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex flex-col h-full pt-20 pb-8 px-6 overflow-y-auto">
            {/* Nav Items */}
            <nav className="flex-1 space-y-1">
              {navItems.map((item, index) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center justify-between py-3.5 px-4 text-base font-montserrat font-medium rounded-xl transition-all duration-300 ${
                    location.pathname === item.path
                      ? 'text-gold-600 bg-gold-50'
                      : 'text-plum-800 hover:text-gold-600 hover:bg-lilac-50'
                  }`}
                  style={{ 
                    transitionDelay: isMobileMenuOpen ? `${index * 30}ms` : '0ms',
                    opacity: isMobileMenuOpen ? 1 : 0,
                    transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(20px)'
                  }}
                >
                  {item.label}
                  {location.pathname === item.path && (
                    <span className="w-2 h-2 rounded-full bg-gold-500" />
                  )}
                </Link>
              ))}
            </nav>

            {/* Footer */}
            <div className="pt-6 mt-6 border-t border-lilac-200">
              <p className="text-center text-sm text-plum-600 font-montserrat">
                Nelly <span className="text-gold-500">&</span> Cyrille
              </p>
              <p className="text-center text-xs text-plum-500 font-montserrat mt-1">
                14 août 2026
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
